import { Controller } from '@hotwired/stimulus'

// Step-by-step donate form: fund, then amount and frequency (with a monthly
// nudge), then details and the embedded Stripe Payment Element.
export default class extends Controller {
  static targets = [
    'fundStep', 'amountStep', 'detailsStep',
    'chosenFund', 'chosenFundName', 'chosenFundBlurb', 'changeFund',
    'presets', 'amount', 'perMonth', 'nudge', 'nudgeText', 'nudgeAccept', 'amountContinue', 'amountBack',
    'summary', 'feeAmount', 'payment', 'submit', 'submitLabel', 'error', 'detailsBack',
    'fundImage', 'fundPanelName', 'fundPanelBlurb'
  ]
  static values = {
    publishableKey: String, createUrl: String, returnUrl: String, lockedFund: String,
    campaign: String, campaignName: String, campaignBlurb: String, campaignImage: String
  }

  connect() {
    this.form = this.element.querySelector('form')
    this.selectedPreset = null
    this.nudgeDecided = false
    this.renderPresets()
    this.updateFundPanel()
    this.form.addEventListener('submit', (e) => this.submit(e))
    const amount = Number(new URLSearchParams(location.search).get('amount'))
    if (amount >= 100) {
      this.selectedPreset = amount
      this.renderPresets()
    }
    this.onPopstate = (event) => this.restoreStep(event)
    window.addEventListener('popstate', this.onPopstate)

    const params = new URLSearchParams(location.search)
    const monthly = params.get('frequency') === 'monthly'
    if (monthly) this.form.querySelector('input[name="frequency"][value="monthly"]').checked = true
    const locked = this.lockedFundValue || this.campaignValue
    const initial = locked && (this.campaignValue || params.get('step') !== 'fund') ? 'amount' : 'fund'
    // Turbo state on this entry would turn in-wizard Back into a Turbo
    // restoration visit that re-fetches the page. Hold it aside so step
    // changes stay instant, and put it back when a real visit leaves.
    this.turboState = history.state?.turbo
    history.replaceState({ donateStep: initial }, '')
    this.reinstateTurboState = () => {
      if (this.turboState) history.replaceState({ ...history.state, turbo: this.turboState }, '')
    }
    document.addEventListener('turbo:before-visit', this.reinstateTurboState)
    if (initial === 'amount') {
      this.toAmountStep({ push: false })
      if (params.get('step') === 'details' && this.amountCents() >= 100) {
        if (monthly) this.nudgeDecided = true
        this.toDetailsStep({ push: false })
      }
    }

    const restoreHeight = window.donateRestoreHeight
    delete window.donateRestoreHeight
    if (restoreHeight) this.animateHeight(restoreHeight)
  }

  disconnect() {
    window.removeEventListener('popstate', this.onPopstate)
    document.removeEventListener('turbo:before-visit', this.reinstateTurboState)
  }

  // --- Step navigation. Each forward move adds a history entry, so the
  // browser's Back button walks the wizard backwards instead of leaving. ---

  stepUrl(step) {
    const params = new URLSearchParams()
    if (this.campaignValue) {
      params.set('campaign', this.campaignValue)
    } else {
      params.set('fund', this.fundInput().value)
    }
    params.set('step', step)
    const amount = this.amountCents()
    if (amount >= 100) params.set('amount', amount)
    if (this.frequency() === 'monthly') params.set('frequency', 'monthly')
    return `${location.pathname}?${params}`
  }

  showStep(step, { push = true } = {}) {
    this.currentStep = step
    if (push) history.pushState({ donateStep: step }, '', this.stepUrl(step))
    const before = this.form.offsetHeight
    this.fundStepTarget.classList.toggle('hidden', step !== 'fund')
    this.amountStepTarget.classList.toggle('hidden', step !== 'amount')
    this.detailsStepTarget.classList.toggle('hidden', step !== 'details')
    const chosen = step !== 'fund'
    this.chosenFundTarget.classList.toggle('hidden', !chosen)
    this.chosenFundTarget.classList.toggle('flex', chosen)
    this.animateHeight(before)
  }

  // Steps differ a lot in height, so the card eases between them rather
  // than snapping.
  animateHeight(before) {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return
    // A still-running animation pins an inline height that would make the
    // form measure wrong, so clear it before reading the target height.
    clearTimeout(this.heightTimer)
    this.form.style.height = this.form.style.transition = this.form.style.overflow = ''
    const after = this.form.offsetHeight
    if (before === after) return
    this.form.style.height = `${before}px`
    this.form.style.overflow = 'hidden'
    this.form.offsetHeight
    this.form.style.transition = 'height 250ms ease'
    this.form.style.height = `${after}px`
    this.heightTimer = setTimeout(() => {
      this.form.style.height = this.form.style.transition = this.form.style.overflow = ''
    }, 280)
  }

  restoreStep(event) {
    const step = event.state?.donateStep
    if (!step) return
    // Entries that also carry Turbo state get a Turbo restoration visit that
    // replaces the body. Leave the height for the next controller instance so
    // the fresh form can ease from it.
    if (event.state?.turbo) {
      window.donateRestoreHeight = this.form.getBoundingClientRect().height
      return
    }
    // Turbo rewrites the popped entry's state with its own, which would turn
    // the next Back into a page-replacing restoration visit. Reclaim it.
    history.replaceState({ donateStep: step }, '')
    if (step === 'amount') return this.toAmountStep({ push: false })
    if (step === 'details') return this.toDetailsStep({ push: false })
    this.showStep(step, { push: false })
  }

  toAmountStep({ push = true } = {}) {
    this.chosenFundNameTarget.textContent = this.designation().name
    this.chosenFundBlurbTarget.textContent = this.designation().blurb
    this.updateFundPanel()
    this.renderPresets()
    this.showStep('amount', { push })
  }

  toDetailsStep({ push = true } = {}) {
    const amount = this.amountCents()
    if (!amount || amount < 100) return this.showError('Please choose or enter an amount of at least $1.')
    this.showError('')

    if (push && this.frequency() === 'once' && !this.nudgeDecided) return this.showNudge(amount)

    this.summaryTarget.textContent = this.summaryText(amount)
    this.feeAmountTarget.textContent = `$${(this.feeCents(amount) / 100).toFixed(2)}`
    this.showStep('details', { push })
    this.mountPayment()
  }

  backToFund() {
    history.back()
  }

  backToAmount() {
    history.back()
  }

  changeFund() {
    if (this.campaignValue) return
    history.replaceState({ donateStep: 'fund' }, '', this.stepUrl('fund'))
    this.lockedFundValue = ''
    this.showStep('fund', { push: false })
  }

  // --- Fund and presets ---

  fundInput() {
    return this.form.querySelector('input[name="fund"]:checked')
  }

  // A locked campaign takes over the designation. Presets still come from its
  // fund's radio (or the default fund's, for standalone campaigns).
  designation() {
    const fund = this.fundInput().dataset
    if (!this.campaignValue) return fund
    return {
      name: this.campaignNameValue,
      blurb: this.campaignBlurbValue,
      image: this.campaignImageValue || fund.image,
      presets: fund.presets
    }
  }

  fundChanged() {
    this.renderPresets()
    this.updateFundPanel()
    this.syncUrl()
  }

  // Selections made after a history entry was pushed rewrite that entry's URL,
  // so Back and reload land on what was actually chosen.
  syncUrl() {
    history.replaceState(history.state, '', this.stepUrl(this.currentStep || 'fund'))
  }

  updateFundPanel() {
    const designation = this.designation()
    if (this.hasFundImageTarget) {
      this.fundImageTarget.src = designation.image
      this.fundPanelNameTarget.textContent = designation.name
      this.fundPanelBlurbTarget.textContent = designation.blurb
    }
  }

  renderPresets() {
    const presets = (this.designation().presets || '').split(',').filter(Boolean).map(Number)
    if (this.selectedPreset && !presets.includes(this.selectedPreset)) {
      this.amountTarget.value = this.selectedPreset / 100
      this.selectedPreset = null
    }
    this.presetsTarget.innerHTML = ''
    for (const cents of presets) {
      const button = document.createElement('button')
      button.type = 'button'
      button.textContent = `$${(cents / 100).toLocaleString()}`
      button.className = this.presetClass(cents === this.selectedPreset && !this.amountTarget.value)
      button.addEventListener('click', () => {
        this.selectedPreset = cents
        this.amountTarget.value = ''
        this.nudgeDecided = false
        this.hideNudge()
        this.renderPresets()
        this.syncUrl()
      })
      this.presetsTarget.appendChild(button)
    }
    this.updatePerMonth()
  }

  presetClass(selected) {
    const base = 'rounded-xl px-4 py-3 text-base/7 font-semibold '
    return selected
      ? base + 'bg-olive-950 text-white dark:bg-olive-300 dark:text-olive-950'
      : base + 'bg-olive-950/5 text-olive-950 hover:bg-olive-950/10 dark:bg-white/10 dark:text-white'
  }

  amountTyped() {
    this.selectedPreset = null
    this.nudgeDecided = false
    this.hideNudge()
    this.renderPresets()
    this.syncUrl()
  }

  frequencyChanged() {
    this.nudgeDecided = false
    this.hideNudge()
    this.updatePerMonth()
    this.syncUrl()
  }

  updatePerMonth() {
    this.perMonthTarget.textContent = this.frequency() === 'monthly' ? 'per month' : ''
  }

  frequency() {
    return this.form.querySelector('input[name="frequency"]:checked').value
  }

  // A filled custom field always wins over a remembered preset.
  amountCents() {
    if (this.amountTarget.value) return Math.round(Number(this.amountTarget.value) * 100)
    return this.selectedPreset || 0
  }

  feeCents(amount) {
    return Math.round(amount * 0.03 + 30)
  }

  chargeCents() {
    const amount = this.amountCents()
    const covered = this.form.querySelector('input[name="cover_fees"]').checked
    return covered ? amount + this.feeCents(amount) : amount
  }

  feesChanged() {
    this.elements?.update({ amount: this.chargeCents() })
  }

  summaryText(amount) {
    const dollars = `$${(amount / 100).toLocaleString()}`
    const cadence = this.frequency() === 'monthly' ? ' each month' : ''
    return `${dollars}${cadence} to ${this.designation().name}.`
  }

  // --- Monthly nudge ---

  showNudge(amount) {
    this.monthlySuggestion = this.suggestMonthly(amount)
    const dollars = `$${(this.monthlySuggestion / 100).toLocaleString()}`
    this.nudgeTextTarget.textContent = `Would you consider ${dollars}/month instead?`
    this.nudgeAcceptTarget.textContent = `Give ${dollars} monthly`
    this.nudgeTarget.classList.remove('lg:invisible', 'max-lg:hidden')
  }

  hideNudge() {
    if (this.hasNudgeTarget) this.nudgeTarget.classList.add('lg:invisible', 'max-lg:hidden')
  }

  suggestMonthly(amount) {
    const quarter = Math.round(amount / 4 / 500) * 500
    return Math.max(500, quarter)
  }

  acceptNudge() {
    this.nudgeDecided = true
    this.form.querySelector('input[name="frequency"][value="monthly"]').checked = true
    this.selectedPreset = this.monthlySuggestion
    this.amountTarget.value = ''
    this.hideNudge()
    this.renderPresets()
    this.updatePerMonth()
    this.syncUrl()
    this.toDetailsStep()
  }

  declineNudge() {
    this.nudgeDecided = true
    this.hideNudge()
    this.toDetailsStep()
  }

  // --- Payment. The card form renders with no server objects; the single
  // Donate click sends a confirmation token and the server creates and
  // confirms everything in one round trip. ---

  async mountPayment() {
    await this.ensureStripe()
    const styles = getComputedStyle(document.documentElement)
    this.elements = this.stripe.elements({
      mode: this.frequency() === 'monthly' ? 'subscription' : 'payment',
      amount: this.chargeCents(),
      currency: 'usd',
      appearance: {
        variables: {
          colorPrimary: styles.getPropertyValue('--color-olive-950').trim(),
          borderRadius: '12px',
          fontFamily: 'Inter, system-ui, sans-serif'
        }
      }
    })
    // Name and email come from our own details form, sent with the token.
    this.paymentTarget.innerHTML = ''
    this.elements.create('payment', {
      fields: { billingDetails: { name: 'never', email: 'never' } }
    }).mount(this.paymentTarget)
    this.paymentTarget.classList.remove('hidden')
  }

  async submit(event) {
    event.preventDefault()
    if (this.busy) return
    this.busy = true
    this.submitTarget.disabled = true
    this.setSubmitState('Processing\u2026', true)
    this.showError('')
    try {
      const { error: submitError } = await this.elements.submit()
      if (submitError) return this.fail(submitError.message)

      const form = new FormData(this.form)
      const { error: tokenError, confirmationToken } = await this.stripe.createConfirmationToken({
        elements: this.elements,
        params: {
          payment_method_data: {
            billing_details: {
              name: form.get('donor_name') || undefined,
              email: form.get('donor_email') || undefined
            }
          }
        }
      })
      if (tokenError) return this.fail(tokenError.message)

      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          fund: this.campaignValue ? null : form.get('fund'),
          amount_cents: this.amountCents(),
          frequency: form.get('frequency'),
          donor_name: form.get('donor_name'),
          donor_email: form.get('donor_email'),
          cover_fees: form.get('cover_fees') === 'on' ? true : null,
          website: form.get('website'),
          campaign: this.campaignValue || null,
          confirmation_token: confirmationToken.id
        })
      })
      const data = await response.json()
      if (!response.ok) return this.fail((data.errors || ['Something went wrong.']).join(' '))

      if (data.status === 'requires_action') {
        const { error } = await this.stripe.handleNextAction({ clientSecret: data.client_secret })
        if (error) return this.fail(error.message)
      }
      const intentId = data.client_secret.split('_secret')[0]
      location.assign(`${this.returnUrlValue}?payment_intent=${intentId}&redirect_status=succeeded`)
    } catch {
      this.fail('Something went wrong. Please try again.')
    }
  }

  fail(message) {
    this.showError(message)
    this.busy = false
    this.submitTarget.disabled = false
    this.setSubmitState('Donate', false)
  }

  // Stripe.js is loaded from the page head, but Turbo visits can connect this
  // controller before that script arrives, so resolve it on demand.
  async ensureStripe() {
    if (!window.Stripe) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = 'https://js.stripe.com/v3/'
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
      })
    }
    this.stripe ||= Stripe(this.publishableKeyValue)
  }

  setSubmitState(label, busy) {
    this.submitLabelTarget.textContent = label
    this.submitTarget.classList.toggle('donate-busy', busy)
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.toggle('hidden', !message)
  }
}
